import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { getFieldsValidations, putFieldsValidateSettings } from '../../../api';
import { UsernameSettings, ValidationRule } from '../../../components';
import {
  ExtendedProjectConfiguration,
  ValidationResponse
} from '../../../components/MainSettings/ValidationRule/types';
import {
  FieldValidationMap,
  getMaxLengthFromRegExp,
  getMinLengthFromRegExp,
  mapFormToFields,
  USERNAME,
  ValidationFieldName
} from '../../../components/MainSettings/ValidationRule/utils';
import {
  ConfigurationSample,
  FieldBlock,
  Preloader,
  ToDashboardError
} from '../../../components/ui';
import { useConfig } from '../../../contexts/config';
import { useCurrentProject } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';

type RouteParams = { projectId: string; confPage: string };

const UsernameSettingsPage = ({ match }: RouteComponentProps): JSX.Element | null => {
  const intl = useIntl();
  const { xsollaAnalytics } = useConfig();
  const { projectId } = match.params as RouteParams;
  const {
    currentProjectFetching,
    currentProjectType,
    updateCurrentProject,
    currentProject,
    currentUpdateFetching,
    error
  } = useCurrentProject(projectId);
  const [fieldsValidation, setFieldsValidation] = useState<ValidationResponse>([]);
  const [fetchingFieldsValidation, setFetchingFieldsValidation] = useState<boolean>(true);
  const [submitError, setSubmitError] = useState<boolean>(false);
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  useEffect(() => {
    getFieldsValidations(projectId).then((res) => {
      setFieldsValidation(res);
      setFetchingFieldsValidation(false);
    });
  }, []);

  const filterUsername = useMemo(
    () =>
      Object.keys(
        fieldsValidation.find((field) => field.name === ValidationFieldName.USERNAME_FIELD)
          ?.settings_state || {}
      ).reduce(
        (prev, key) =>
          FieldValidationMap.username.validationRule.includes(key as USERNAME) && {
            ...prev,
            [key]: true
          },
        {}
      ),
    [fieldsValidation]
  );

  const filterOther = useMemo(
    () =>
      fieldsValidation
        .filter((field) => field.name !== ValidationFieldName.USERNAME_FIELD)
        .map((field) => ({ ...field, regexps: field.regexps ? field.regexps : [] })),
    [fieldsValidation]
  );

  const minLength = useMemo(
    () => getMinLengthFromRegExp(fieldsValidation, ValidationFieldName.USERNAME_FIELD),
    [fieldsValidation]
  );

  const maxLength = useMemo(
    () => getMaxLengthFromRegExp(fieldsValidation, ValidationFieldName.USERNAME_FIELD),
    [fieldsValidation]
  );

  if (error) {
    return <ToDashboardError error={error} pageName="username-settings_open" />;
  }

  if (currentProjectFetching || !currentProject) {
    return <Preloader />;
  }

  const onSubmit = async (values: ExtendedProjectConfiguration) => {
    const {
      fields_validation,
      validation_max_length,
      validation_min_length,
      ...configurationValues
    } = values;

    try {
      setSubmitError(false);

      const passValidation = [
        ...filterOther,
        {
          name: ValidationFieldName.USERNAME_FIELD,
          ...mapFormToFields({ fields_validation, validation_max_length, validation_min_length })
        }
      ];

      await Promise.all([
        updateCurrentProject(configurationValues),
        putFieldsValidateSettings({ projectId, values: passValidation })
      ]);

      showSuccessNotify();
      xsollaAnalytics.sendClick('login_pa_module__username_validation_page', 'success', {
        usernameValidation: Object.entries(fields_validation)
          .filter(([, field]) => !!field)
          .map(([key]) => key)
      });
      setFieldsValidation(passValidation);
    } catch (err) {
      showErrorNotify();
      xsollaAnalytics.sendClick('login_pa_module__username_validation_page', 'error', {
        error: err
      });
      setSubmitError(true);
    }
  };

  return (
    <ConfigurationSample
      currentProject={currentProject}
      initialValues={{
        ...currentProject,
        fields_validation: filterUsername,
        validation_min_length: minLength,
        validation_max_length: maxLength
      }}
      currentProjectType={currentProjectType}
      currentProjectFetching={fetchingFieldsValidation}
      currentUpdateFetching={currentUpdateFetching}
      onSubmit={onSubmit}
      submitError={submitError}
      title={intl.formatMessage({ id: 'configuration-page.username.page-title' })}
      description={intl.formatMessage({ id: 'configuration-page.username.page-description' })}
      notification={intl.formatMessage(
        {
          id: 'configuration-page.username.page-notification'
        },
        {
          ul: (chunk: string) => <ul style={{ padding: '0 0 0 24px', margin: '0' }}>{chunk}</ul>,
          li: (chunk: string) => <li>{chunk}</li>
        }
      )}>
      <>
        <FieldBlock
          title={intl.formatMessage({
            id: 'configuration-page.username.username-validation.title'
          })}
          description={intl.formatMessage({
            id: 'configuration-page.username.username-validation.description'
          })}
          notification={intl.formatMessage({
            id: 'configuration-page.username.username-validation.notification'
          })}
          contentMargin="16px">
          <ValidationRule name={ValidationFieldName.USERNAME_FIELD} />
        </FieldBlock>
        <UsernameSettings />
      </>
    </ConfigurationSample>
  );
};

export default withRouter(UsernameSettingsPage);
